/**
 * Entrada de material — a tela do celular, no recebimento.
 *
 * Alguém está de pé no galpão, com o material recém-descarregado na frente e o
 * celular numa mão. Tudo aqui é feito para esse contexto:
 *
 *   BOTÕES GRANDES, poucos, e a câmera abre direto — sem menu, sem escolher
 *   arquivo. Um toque para fotografar, um para enviar.
 *
 *   O ENVIO NÃO ESPERA A IA. Grava a entrada primeiro e pede a leitura depois,
 *   sem a pessoa precisar ficar olhando. No sistema atual as duas coisas são a
 *   mesma chamada, e o celular fica travado de 10 a 30 segundos — se a conexão
 *   cair no meio, some tudo, inclusive a foto. Aqui, se a IA falhar, a entrada
 *   já está registrada e vai para a fila sem palpite.
 *
 *   FUNCIONA COM POUCO SINAL. A foto sobe primeiro e sozinha; só depois o
 *   registro. Se o sinal cair na foto, nada foi criado pela metade.
 */

import { api, ErroApi } from '../api.js';
import { escapar, quando } from '../comum/formato.js';

type Tipo = 'NF' | 'Material sem NF';

interface Enviada {
  id: number;
  tipo: Tipo;
  quandoIso: string;
  miniatura: string;
  analise: 'esperando' | 'pronta' | 'falhou';
  resumo?: string;
}

export function montarEntrada(raiz: HTMLElement): void {
  let tipo: Tipo = 'NF';
  let arquivo: File | undefined;
  // Só desta sessão — é confirmação do que acabou de sair da mão da pessoa,
  // não histórico. O histórico completo vive na fila de Aprovações.
  const enviadas: Enviada[] = [];

  raiz.innerHTML = `
    <div class="entrada">
      <div class="entrada-tipo" id="tipo"></div>

      <div class="entrada-foto" id="previa">
        <p class="entrada-vazio">Nenhuma foto ainda.</p>
      </div>

      <label class="btn btn-bloco btn-grande arquivo">
        📷 Tirar foto
        <input type="file" accept="image/*" capture="environment" id="camera" hidden>
      </label>

      <label class="rotulado">
        <span>Observação (opcional)</span>
        <input id="obs" maxlength="500" placeholder="Ex.: chegou 1 caixa amassada">
      </label>

      <button class="btn btn-bloco btn-grande" id="enviar" disabled>Enviar</button>
      <p class="aviso" id="erro" hidden></p>
      <p class="dica" id="estado"></p>

      <div id="enviadas"></div>
    </div>`;

  const previa = raiz.querySelector<HTMLElement>('#previa')!;
  const camera = raiz.querySelector<HTMLInputElement>('#camera')!;
  const botao = raiz.querySelector<HTMLButtonElement>('#enviar')!;
  const erro = raiz.querySelector<HTMLElement>('#erro')!;
  const estado = raiz.querySelector<HTMLElement>('#estado')!;
  const obs = raiz.querySelector<HTMLInputElement>('#obs')!;

  function desenharTipo() {
    raiz.querySelector<HTMLElement>('#tipo')!.innerHTML = (
      [
        ['NF', '🧾 Com nota fiscal', 'A IA lê fornecedor, número e a lista de itens.'],
        ['Material sem NF', '📦 Sem nota', 'A IA tenta identificar o material pela foto.'],
      ] as [Tipo, string, string][]
    )
      .map(
        ([v, t, d]) => `
        <button class="entrada-opcao" data-t="${v}" aria-pressed="${tipo === v}">
          <b>${t}</b><small>${escapar(d)}</small>
        </button>`,
      )
      .join('');
  }

  raiz.querySelector('#tipo')!.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-t]');
    if (!b) return;
    tipo = b.dataset['t'] as Tipo;
    desenharTipo();
  });

  camera.addEventListener('change', () => {
    const f = camera.files?.[0];
    if (!f) return;
    arquivo = f;
    esconderErro();
    // `createObjectURL` mostra a foto sem ler o arquivo inteiro na memória —
    // num celular com foto de 8 MB, ler para base64 só para pré-visualizar é
    // justamente o que trava a tela.
    previa.innerHTML = `<img src="${URL.createObjectURL(f)}" alt="Foto tirada agora">`;
    botao.disabled = false;
  });

  botao.addEventListener('click', () => void enviar());

  async function enviar() {
    if (!arquivo) return;
    botao.disabled = true;
    esconderErro();

    try {
      // 1) a foto sobe sozinha. Se cair aqui, nada foi criado pela metade.
      estado.textContent = 'Enviando a foto…';
      const foto = await api.enviarFoto('notas', arquivo, tipo === 'NF' ? 'nf' : 'material');

      // 2) o registro é gravado. A partir daqui a entrada existe, aconteça o
      //    que acontecer com a IA.
      estado.textContent = 'Registrando…';
      const criada = await api.post<{ id: number }>('/api/aprovacoes', {
        tipo,
        foto: foto.caminho,
        ...(obs.value.trim() ? { obs: obs.value.trim() } : {}),
      });

      const registro: Enviada = {
        id: criada.id,
        tipo,
        quandoIso: new Date().toISOString(),
        miniatura: foto.url,
        analise: 'esperando',
      };
      enviadas.unshift(registro);

      // Já pode fotografar a próxima: o formulário limpa agora, e a leitura da
      // IA segue em segundo plano.
      arquivo = undefined;
      camera.value = '';
      obs.value = '';
      previa.innerHTML = `<p class="entrada-vazio">Nenhuma foto ainda.</p>`;
      estado.textContent = '';
      desenharEnviadas();

      void analisar(registro);
    } catch (e) {
      botao.disabled = false;
      estado.textContent = '';
      mostrarErro(
        e instanceof ErroApi
          ? e.message
          : 'Não consegui enviar. Confira o sinal e tente de novo — a foto não foi perdida.',
      );
    }
  }

  /**
   * Pede a leitura da IA. Falhar aqui é aceitável e a tela diz isso: a entrada
   * já está na fila, e quem for conferir pode mandar ler de novo.
   */
  async function analisar(registro: Enviada) {
    try {
      const r = await api.post<{ palpite: Record<string, unknown> }>(
        `/api/aprovacoes/${registro.id}/analise`,
        {},
      );
      const p = r.palpite ?? {};
      registro.analise = p['erro'] ? 'falhou' : 'pronta';
      registro.resumo = p['erro']
        ? String(p['erro'])
        : p['fornecedor']
          ? `${String(p['fornecedor'])} · ${(p['itens'] as unknown[] | undefined)?.length ?? 0} item(ns)`
          : String(p['palpite_nome'] ?? 'lido');
    } catch {
      registro.analise = 'falhou';
      registro.resumo = 'A leitura não foi feita — a entrada está na fila mesmo assim.';
    }
    desenharEnviadas();
  }

  function desenharEnviadas() {
    const alvo = raiz.querySelector<HTMLElement>('#enviadas')!;
    if (enviadas.length === 0) {
      alvo.innerHTML = '';
      return;
    }
    alvo.innerHTML = `
      <h3>Enviados agora</h3>
      <p class="dica">Já estão na fila de conferência. Não precisa esperar aqui.</p>
      ${enviadas
        .map(
          (e) => `
        <div class="enviada">
          <img src="${escapar(api.foto(e.miniatura) ?? '')}" alt="">
          <div>
            <b>${escapar(e.tipo)}</b>
            <span class="sub">${quando(e.quandoIso)}</span>
            <span class="enviada-estado e-${e.analise}">
              ${
                e.analise === 'esperando'
                  ? 'lendo a foto…'
                  : e.analise === 'pronta'
                    ? `✓ ${escapar(e.resumo ?? '')}`
                    : `⚠ ${escapar(e.resumo ?? '')}`
              }
            </span>
          </div>
        </div>`,
        )
        .join('')}`;
  }

  function mostrarErro(msg: string) {
    erro.textContent = msg;
    erro.hidden = false;
  }
  function esconderErro() {
    erro.hidden = true;
  }

  desenharTipo();
}
